'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { useAuthStore } from '@/stores/auth';
import { Compass, LayoutDashboard, Search } from 'lucide-react';

type NotFoundPageProps = {
  /** When provided, shows a "Try again" button (e.g. in error boundary). */
  onRetry?: () => void;
};

/**
 * Shared 404 / not-found UI. Used by not-found.tsx and error.tsx
 * so all 4XX-style errors show the same page.
 */
export function NotFoundPage({ onRetry }: NotFoundPageProps) {
  const t = useTranslations('notFoundPage');
  const authStore = useAuthStore();

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] dark:bg-slate-950 px-4 py-12">
      <div className="max-w-2xl w-full text-center relative">
        {/* Floating Compass Icon */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <Compass
              size={120}
              className="text-blue-600 dark:text-blue-400 animate-[float_3s_ease-in-out_infinite]"
              strokeWidth={1.5}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Heading */}
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100">
            {t('header')}
          </h1>

          {/* Description */}
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-lg mx-auto">
            {t('subtitle')}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            {/* Primary Button - Dashboard (only show if logged in) */}
            {!authStore.authLoading && authStore.isLoggedIn && (
              <Link
                href="/dashboard"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
              >
                <LayoutDashboard size={20} />
                {t('backToDashboard')}
              </Link>
            )}

            {/* Secondary Button - Find Cruise */}
            <Link
              href="/cruises"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 border-2 border-blue-600 dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-semibold rounded-lg transition-all shadow-md hover:shadow-lg transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
            >
              <Search size={20} />
              {t('findCruise')}
            </Link>

            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Try again
              </button>
            )}
          </div>

          {/* Easter Egg Footer */}
          <footer className="mt-16">
            <p className="text-xs text-slate-400 dark:text-slate-500 italic">
              {t('easterEgg')}
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}
